import { useQuery } from '@tanstack/react-query';

import { assistantCapabilities } from '@/api/assistant';
import { useAuth } from '@/auth/useAuth';

/** The workspace's switch changes rarely, so the answer is kept for a while. */
const CAPABILITIES_STALE_MS = 5 * 60_000;

/**
 * Whether this person has an assistant to open.
 *
 * It is true only once the server has said the assistant is enabled for the workspace and the
 * person may use it. Until then, and whenever either is not so, the answer is no, and the shell
 * draws no control for it: a button that only leads to "disabled" is clutter. The query key is the
 * one the panel reads capabilities under, so opening the panel costs no second request.
 *
 * @returns true when the assistant should be offered
 */
export function useAssistantAvailability(): boolean {
  const { can, identity } = useAuth();
  const allowed = can('AI_ASSIST_READ');
  const capabilities = useQuery({
    queryKey: ['assistant', identity, 'capabilities'],
    queryFn: assistantCapabilities,
    enabled: allowed && identity !== null,
    staleTime: CAPABILITIES_STALE_MS,
  });
  return allowed && capabilities.data?.enabled === true;
}
